'use client';

import { useSyncExternalStore } from 'react';

/**
 * What the map is currently pointing at: a selection, a hover, and the flow
 * being narrated.
 *
 * A module singleton read through `useSyncExternalStore` rather than context,
 * because three separated surfaces — the rail, the canvas and the panel — all
 * read and write it, and threading a provider between them buys nothing.
 *
 * `getSnapshot` must return a referentially stable value or React will loop:
 * the state object is replaced only when something actually changes.
 */













const EMPTY = {
  selection: null,
  hover: null,
  hoverGroup: null,
  activeFlowId: null,
  resetTick: 0
};

let state = EMPTY;
const listeners = new Set();

function set(next) {
  const merged = { ...state, ...next };
  // Cheap identity guard: hovering the same thing twice must not re-render.
  if (
  merged.selection === state.selection &&
  merged.hover === state.hover &&
  merged.hoverGroup === state.hoverGroup &&
  merged.activeFlowId === state.activeFlowId &&
  merged.resetTick === state.resetTick)
  {
    return;
  }
  state = merged;
  for (const listener of listeners) listener();
}

export function select(selection) {
  // Choosing a thing stops the narration: a flow talking over the module you
  // just asked about is the page arguing with itself.
  set({ selection, activeFlowId: selection ? null : state.activeFlowId });
}

export function setHover(hover) {
  set({ hover });
}

export function setHoverGroup(hoverGroup) {
  set({ hoverGroup });
}

export function setActiveFlow(activeFlowId) {
  set({ activeFlowId, selection: null });
}

export function clearView() {
  set({ selection: null, hover: null, activeFlowId: null, resetTick: state.resetTick + 1 });
}

export function hasFocus(view) {
  return view.selection !== null || view.activeFlowId !== null;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useMapView() {
  return useSyncExternalStore(subscribe, () => state, () => EMPTY);
}